'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, AlertTriangle, AlertCircle, CheckCircle, RefreshCw, Loader2, Search, Filter, Shield, Check, ChevronDown, Info, Lock, UserX, Activity, Clock } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface AuthAlert {
  id: string;
  created_at: string;
  alert_type: string;
  severity: string;
  title: string;
  description: string | null;
  user_email: string | null;
  company_name: string | null;
  ip_address: string | null;
  is_read: boolean;
  is_resolved: boolean;
  resolved_at: string | null;
  metadata: Record<string, unknown> | null;
}

const SEVERITY_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  critical: { label: 'حرج',    color: 'text-red-700',    bg: 'bg-red-50 border-red-200',    icon: AlertCircle },
  warning:  { label: 'تحذير',  color: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200', icon: AlertTriangle },
  info:     { label: 'معلومة', color: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200',   icon: Info },
  success:  { label: 'نجاح',   color: 'text-green-700',  bg: 'bg-green-50 border-green-200', icon: CheckCircle },
};

const ALERT_TYPE_LABELS: Record<string, string> = {
  repeated_login_failure:  'محاولات دخول متكررة',
  suspicious_login:        'دخول مشبوه',
  account_locked:          'حساب مقفل',
  password_brute_force:    'هجوم كلمة مرور',
  unauthorized_access:     'وصول غير مصرح',
  multiple_failed_attempts:'محاولات فاشلة متعددة',
  new_device_login:        'دخول من جهاز جديد',
  admin_action:            'إجراء إداري',
};

const PAGE_SIZE = 30;

const formatDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('ar-SA', { dateStyle: 'short', timeStyle: 'short' });
};

export default function AuthAlertCenter({ lang }: { lang: 'ar' | 'en' }) {
  const supabase = createClient();
  const [alerts, setAlerts] = useState<AuthAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState<'all' | 'unresolved' | 'resolved'>('unresolved');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, unresolved: 0, critical: 0, warning: 0 });

  const fetchAlerts = useCallback(async (off = 0, append = false) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_auth_alerts', {
        p_limit: PAGE_SIZE + 1,
        p_offset: off,
        p_severity: severityFilter !== 'all' ? severityFilter : null,
        p_is_resolved: resolvedFilter === 'all' ? null : resolvedFilter === 'resolved',
        p_search: search.trim() || null,
      });

      if (error) throw error;
      const rows = (data || []) as AuthAlert[];
      const more = rows.length > PAGE_SIZE;
      const trimmed = more ? rows.slice(0, PAGE_SIZE) : rows;
      setHasMore(more);
      setAlerts(prev => append ? [...prev, ...trimmed] : trimmed);
      setOffset(off);

      // Compute stats from first load
      if (!append && off === 0) {
        const { data: allData } = await supabase
          .from('auth_alerts')
          .select('severity, is_resolved');
        if (allData) {
          const total = allData.length;
          const unresolved = allData.filter(a => !a.is_resolved).length;
          const critical = allData.filter(a => a.severity === 'critical').length;
          const warning = allData.filter(a => a.severity === 'warning').length;
          setStats({ total, unresolved, critical, warning });
        }
      }
    } catch {
      // Fallback: direct query
      try {
        let q = supabase
          .from('auth_alerts')
          .select('*')
          .order('created_at', { ascending: false })
          .range(off, off + PAGE_SIZE);
        if (severityFilter !== 'all') q = q.eq('severity', severityFilter);
        if (resolvedFilter !== 'all') q = q.eq('is_resolved', resolvedFilter === 'resolved');
        if (search.trim()) q = q.ilike('user_email', `%${search.trim()}%`);
        const { data } = await q;
        const rows = (data || []) as AuthAlert[];
        setAlerts(prev => append ? [...prev, ...rows] : rows);
        setHasMore(rows.length === PAGE_SIZE + 1);
      } catch {
        setAlerts([]);
      }
    } finally {
      setLoading(false);
    }
  }, [supabase, severityFilter, resolvedFilter, search]);

  useEffect(() => { fetchAlerts(0); }, [fetchAlerts]);

  const handleResolve = async (alertId: string) => {
    setResolvingId(alertId);
    try {
      const { error } = await supabase.rpc('resolve_auth_alert', { p_alert_id: alertId });
      if (error) throw error;
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_resolved: true, resolved_at: new Date().toISOString(), is_read: true } : a));
      setStats(prev => ({ ...prev, unresolved: Math.max(0, prev.unresolved - 1) }));
      toast.success(lang === 'ar' ? 'تم حل التنبيه' : 'Alert resolved');
    } catch {
      toast.error(lang === 'ar' ? 'خطأ في حل التنبيه' : 'Error resolving alert');
    } finally {
      setResolvingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await supabase.rpc('mark_auth_alerts_read');
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      toast.success(lang === 'ar' ? 'تم تعليم الكل كمقروء' : 'All marked as read');
    } catch {
      toast.error(lang === 'ar' ? 'خطأ' : 'Error');
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0D1117] flex items-center gap-2">
            <Bell size={20} className="text-[#0F5C5C]" />
            {lang === 'ar' ? 'مركز تنبيهات المصادقة' : 'Auth Alert Center'}
          </h1>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {lang === 'ar' ? 'رصد التنبيهات الأمنية ومحاولات الدخول المشبوهة' : 'Monitor security alerts and suspicious login attempts'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleMarkAllRead}
            className="flex items-center gap-1.5 h-9 px-3 bg-white border border-[#E2E6EA] text-[#6B7280] rounded-xl text-sm font-semibold hover:bg-[#F0F2F5] transition-colors"
          >
            <Check size={13} />
            {lang === 'ar' ? 'تعليم الكل كمقروء' : 'Mark All Read'}
          </button>
          <button
            onClick={() => fetchAlerts(0)}
            className="flex items-center gap-1.5 h-9 px-3 bg-[#EAF4F4] text-[#0F5C5C] rounded-xl text-sm font-semibold hover:bg-[#D0E8E8] transition-colors"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            {lang === 'ar' ? 'تحديث' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: lang === 'ar' ? 'إجمالي التنبيهات' : 'Total Alerts',    value: stats.total,      icon: Bell,          bg: 'bg-[#EAF4F4]', color: 'text-[#0F5C5C]' },
          { label: lang === 'ar' ? 'غير محلولة' : 'Unresolved',            value: stats.unresolved, icon: AlertTriangle, bg: 'bg-amber-50',   color: 'text-amber-600' },
          { label: lang === 'ar' ? 'حرجة' : 'Critical',                    value: stats.critical,   icon: AlertCircle,   bg: 'bg-red-50',     color: 'text-red-600' },
          { label: lang === 'ar' ? 'تحذيرات' : 'Warnings',                 value: stats.warning,    icon: Shield,        bg: 'bg-blue-50',    color: 'text-blue-600' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-[#E2E6EA] rounded-2xl p-4 shadow-sm">
            <div className={`w-9 h-9 rounded-xl ${card.bg} flex items-center justify-center mb-2`}>
              <card.icon size={16} className={card.color} />
            </div>
            <p className="text-xl font-bold text-[#0D1117]">{card.value}</p>
            <p className="text-xs text-[#6B7280] mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={13} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#9CA3AF]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchAlerts(0)}
            placeholder={lang === 'ar' ? 'بحث بالبريد أو العنوان...' : 'Search by email or title...'}
            className="w-full h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 pe-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
          />
        </div>
        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
        >
          <option value="all">{lang === 'ar' ? 'كل الخطورة' : 'All Severities'}</option>
          <option value="critical">{lang === 'ar' ? 'حرج' : 'Critical'}</option>
          <option value="warning">{lang === 'ar' ? 'تحذير' : 'Warning'}</option>
          <option value="info">{lang === 'ar' ? 'معلومة' : 'Info'}</option>
        </select>
        <select
          value={resolvedFilter}
          onChange={e => setResolvedFilter(e.target.value as typeof resolvedFilter)}
          className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
        >
          <option value="all">{lang === 'ar' ? 'الكل' : 'All'}</option>
          <option value="unresolved">{lang === 'ar' ? 'غير محلولة' : 'Unresolved'}</option>
          <option value="resolved">{lang === 'ar' ? 'محلولة' : 'Resolved'}</option>
        </select>
        <button
          onClick={() => fetchAlerts(0)}
          className="h-9 px-3 bg-[#0F5C5C] text-white rounded-xl text-sm font-semibold hover:bg-[#0A3D3D] transition-colors flex items-center gap-1.5"
        >
          <Filter size={13} />
          {lang === 'ar' ? 'تطبيق' : 'Apply'}
        </button>
      </div>

      {/* Alerts list */}
      <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
        {loading && alerts.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-[#0F5C5C]" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#9CA3AF]">
            <Bell size={32} className="mb-2 opacity-40" />
            <p className="text-sm">{lang === 'ar' ? 'لا توجد تنبيهات' : 'No alerts found'}</p>
            <p className="text-xs mt-1 text-center max-w-xs">
              {lang === 'ar' ?'ستظهر التنبيهات الأمنية هنا عند رصد نشاط مشبوه' :'Security alerts will appear here when suspicious activity is detected'}
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-[#F0F2F5]">
              {alerts.map(alert => {
                const sev = SEVERITY_CONFIG[alert.severity] ?? SEVERITY_CONFIG.info;
                const SevIcon = sev.icon;
                return (
                  <div
                    key={alert.id}
                    className={`px-5 py-4 hover:bg-[#FAFBFC] transition-colors ${!alert.is_read ? 'border-s-2 border-[#0F5C5C]' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-xl ${sev.bg.split(' ')[0]} border ${sev.bg.split(' ')[1] || ''} flex items-center justify-center shrink-0 mt-0.5`}>
                        <SevIcon size={15} className={sev.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-bold text-[#0D1117]">{alert.title}</p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${sev.bg}`}>
                                <span className={sev.color}>{sev.label}</span>
                              </span>
                              {!alert.is_read && (
                                <span className="w-2 h-2 rounded-full bg-[#0F5C5C] shrink-0" />
                              )}
                              {alert.is_resolved && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
                                  <CheckCircle size={9} />
                                  {lang === 'ar' ? 'محلول' : 'Resolved'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[#6B7280] mt-0.5">
                              {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {!alert.is_resolved && (
                              <button
                                onClick={() => handleResolve(alert.id)}
                                disabled={resolvingId === alert.id}
                                className="flex items-center gap-1 h-7 px-2.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-semibold hover:bg-green-100 transition-colors disabled:opacity-50"
                              >
                                {resolvingId === alert.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                                {lang === 'ar' ? 'حل' : 'Resolve'}
                              </button>
                            )}
                          </div>
                        </div>

                        {alert.description && (
                          <p className="text-xs text-[#6B7280] mt-1.5 leading-relaxed">{alert.description}</p>
                        )}

                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          {alert.user_email && (
                            <span className="flex items-center gap-1 text-[10px] text-[#6B7280]">
                              <UserX size={10} />
                              {alert.user_email}
                            </span>
                          )}
                          {alert.company_name && (
                            <span className="flex items-center gap-1 text-[10px] text-[#6B7280]">
                              <Activity size={10} />
                              {alert.company_name}
                            </span>
                          )}
                          {alert.ip_address && (
                            <span className="flex items-center gap-1 text-[10px] text-[#9CA3AF] font-mono">
                              <Lock size={10} />
                              {alert.ip_address}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-[10px] text-[#9CA3AF]">
                            <Clock size={10} />
                            {formatDate(alert.created_at)}
                          </span>
                          {alert.is_resolved && alert.resolved_at && (
                            <span className="flex items-center gap-1 text-[10px] text-green-600">
                              <CheckCircle size={10} />
                              {lang === 'ar' ? 'حُل في' : 'Resolved'} {formatDate(alert.resolved_at)}
                            </span>
                          )}
                        </div>

                        {/* Metadata */}
                        {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {Object.entries(alert.metadata).slice(0, 4).map(([k, v]) => (
                              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F7F8FA] border border-[#E2E6EA] rounded-lg text-[10px] text-[#6B7280]">
                                <span className="font-semibold">{k}:</span>
                                <span>{String(v)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {hasMore && (
              <div className="p-4 border-t border-[#F0F2F5] flex justify-center">
                <button
                  onClick={() => fetchAlerts(offset + PAGE_SIZE, true)}
                  disabled={loading}
                  className="flex items-center gap-1.5 h-9 px-4 bg-[#EAF4F4] text-[#0F5C5C] rounded-xl text-sm font-semibold hover:bg-[#D0E8E8] transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 size={13} className="animate-spin" /> : <ChevronDown size={13} />}
                  {lang === 'ar' ? 'تحميل المزيد' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
